import { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CertificateView({ user, config }) {
  const [certificateData, setCertificateData] = useState(null);
  const [courseCompletions, setCourseCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const certificateRef = useRef(null);

  useEffect(() => {
    checkCertificateEligibility();
    fetchCourseCompletions();
  }, []);

  const checkCertificateEligibility = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/exams/certificate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificateData(response.data);
    } catch (err) {
      setError(
        err.response?.data?.detail || "Error checking certificate eligibility."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseCompletions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/certificates/completions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseCompletions(response.data.completions || []);
    } catch (err) {
      console.error("Error fetching course completions:", err);
    }
  };

  const printCertificate = () => {
    window.print();
  };

  const downloadAsPDF = () => {
    // Use browser print dialog with PDF option
    const printWindow = window.open('', '_blank');
    const certificateContent = certificateRef.current?.innerHTML;
    
    if (printWindow && certificateContent) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificate - Yisrael Learning Center</title>
          <style>
            @page { size: landscape; margin: 0.5in; }
            body {
              font-family: 'Georgia', 'Times New Roman', serif;
              margin: 0;
              padding: 20px;
              background: #fff;
            }
            .certificate {
              background: linear-gradient(135deg, #fff 0%, #f5f5dc 100%);
              padding: 50px;
              text-align: center;
              border: 8px double #8B4513;
              max-width: 900px;
              margin: 0 auto;
            }
            h2 { font-size: 2rem; color: #8B4513; margin-bottom: 20px; }
            h3 { font-size: 2rem; margin: 15px 0; }
            h4 { font-size: 1.5rem; color: #8B4513; margin: 10px 0; }
            p { color: #333; line-height: 1.6; }
            .seal {
              width: 100px;
              height: 100px;
              margin: 20px auto;
              border: 3px solid #8B4513;
              border-radius: 50%;
              background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .seal-text {
              text-align: center;
              font-weight: bold;
              font-size: 0.6rem;
              color: #000;
            }
            .signature-line {
              border-top: 2px solid #8B4513;
              width: 180px;
              margin: 30px auto 5px;
            }
          </style>
        </head>
        <body>
          ${certificateContent}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <h3>Certificates</h3>
        <div className="spinner"></div>
      </div>
    );
  }

  // Show selected course completion certificate
  if (selectedCertificate) {
    return (
      <div data-testid="course-certificate-view">
        <div className="card no-print">
          <button 
            onClick={() => setSelectedCertificate(null)}
            style={{ background: "#333", marginBottom: "15px" }}
          >
            ← Back to Certificates
          </button>
          <h3>Course Completion Certificate</h3>
          <p style={{ color: "#90EE90", marginBottom: "15px" }}>
            Your certificate is ready to download or print!
          </p>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button data-testid="print-course-certificate" onClick={printCertificate}>
              🖨️ Print Certificate
            </button>
            <button 
              data-testid="download-course-certificate" 
              onClick={downloadAsPDF}
              style={{ background: "#87CEEB", color: "#000" }}
            >
              📥 Download as PDF
            </button>
            {/* iter 68 — one-tap social share */}
            {(() => {
              const shareText = encodeURIComponent(
                `I just completed ${selectedCertificate.subject_name} (${selectedCertificate.grade_level || ""}) at Yisrael Learning Center with a score of ${selectedCertificate.score}%. #YisraelLearningCenter #Enoch72`
              );
              const shareUrl = encodeURIComponent(window.location.origin);
              const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}&summary=${shareText}`;
              const twitterUrl = `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`;
              const whatsappUrl = `https://api.whatsapp.com/send?text=${shareText}%20${shareUrl}`;
              const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`;
              return (
                <>
                  <a
                    data-testid="share-cert-linkedin"
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={_shareBtn("#0a66c2")}
                  >in LinkedIn</a>
                  <a
                    data-testid="share-cert-twitter"
                    href={twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={_shareBtn("#1DA1F2")}
                  >𝕏 Post</a>
                  <a
                    data-testid="share-cert-facebook"
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={_shareBtn("#1877F2")}
                  >f Facebook</a>
                  <a
                    data-testid="share-cert-whatsapp"
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={_shareBtn("#25D366")}
                  >💬 WhatsApp</a>
                </>
              );
            })()}
          </div>
        </div>

        {/* Course Completion Certificate */}
        <div ref={certificateRef} className="certificate" data-testid="course-certificate-content" style={{
          background: "linear-gradient(135deg, #fff 0%, #f5f5dc 100%)",
          padding: "50px",
          textAlign: "center",
          border: "8px double #8B4513",
          margin: "20px auto",
          maxWidth: "800px",
          color: "#333"
        }}>
          <div style={{ 
            borderBottom: "2px solid #8B4513", 
            paddingBottom: "20px",
            marginBottom: "30px"
          }}>
            <h2 style={{ 
              fontSize: "2rem", 
              color: "#8B4513", 
              margin: 0,
              fontFamily: "serif"
            }}>
              CERTIFICATE OF COMPLETION
            </h2>
          </div>
          
          <p style={{ fontSize: "1.1rem", marginTop: "20px", color: "#555" }}>
            This is to certify that
          </p>
          
          <h3 style={{ 
            fontSize: "2.5rem", 
            margin: "15px 0",
            fontFamily: "serif",
            color: "#333"
          }}>
            {user.username}
          </h3>
          
          <p style={{ fontSize: "1.1rem", color: "#555" }}>
            has successfully completed the course
          </p>
          
          <h3 style={{ 
            fontSize: "1.8rem", 
            margin: "15px 0",
            color: "#8B4513",
            fontFamily: "serif"
          }}>
            {selectedCertificate.subject_name}
          </h3>
          
          <p style={{ fontSize: "1rem", color: "#666" }}>
            Grade Level: <strong>{selectedCertificate.grade_level}</strong>
          </p>
          
          <div style={{
            background: "#f0f0e8",
            padding: "15px 30px",
            borderRadius: "8px",
            margin: "20px auto",
            maxWidth: "400px"
          }}>
            <p style={{ margin: "5px 0", color: "#555" }}>
              Exam Score: <strong style={{ color: "#2e7d32" }}>{selectedCertificate.score}%</strong>
            </p>
            <p style={{ margin: "5px 0", color: "#555" }}>
              Completed: <strong>{new Date(selectedCertificate.completed_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
              })}</strong>
            </p>
          </div>
          
          <p style={{ fontSize: "1rem", margin: "25px 0", color: "#555" }}>
            Awarded by
          </p>
          
          <h4 style={{ 
            fontSize: "1.5rem", 
            color: "#8B4513",
            margin: "10px 0",
            fontFamily: "serif"
          }}>
            {config?.CHURCH_NAME || "Yisrael Learning Center"}
          </h4>
          
          {/* Seal */}
          <div style={{ margin: "30px auto" }}>
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/22002ca9-24bb-4912-b3b6-4eb4ba6d87ae/images/5ea2270e4415f9b16eb9c3d5a9c6e790c2d8b49b4cb12d7e58da108a0bb70462.png"
              alt="Official Seal - Yisrael Learning Center"
              style={{
                width: "140px",
                height: "140px",
                margin: "0 auto",
                display: "block"
              }}
            />
          </div>
          
          <div style={{ 
            marginTop: "40px", 
            display: "flex",
            justifyContent: "space-around",
            alignItems: "flex-end"
          }}>
            <div style={{ textAlign: "center" }}>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/22002ca9-24bb-4912-b3b6-4eb4ba6d87ae/images/b19c66f1ad1e9da859918d3a7bbee781b3726e04c5d31cd64e2522dddbaddfef.png"
                alt="Signature"
                style={{
                  width: "150px",
                  height: "auto",
                  marginBottom: "5px"
                }}
              />
              <div style={{ 
                borderTop: "2px solid #8B4513", 
                width: "180px", 
                margin: "0 auto 5px" 
              }}></div>
              <p style={{ color: "#333", fontSize: "0.9rem", fontWeight: "bold" }}>Chief/Priest Ykbn Yoonw</p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ 
                fontSize: "1.1rem", 
                fontWeight: "bold", 
                color: "#333",
                marginBottom: "5px"
              }}>
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </p>
              <div style={{ 
                borderTop: "2px solid #8B4513", 
                width: "180px", 
                margin: "0 auto 5px" 
              }}></div>
              <p style={{ color: "#333", fontSize: "0.9rem", fontWeight: "bold" }}>Date of Completion</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="certificate-view">
      {/* Course Completion Certificates Section */}
      <div className="card" style={{ marginBottom: "20px" }}>
        <h3 style={{ color: "#FFD700", marginBottom: "20px" }}>
          📜 Course Completion Certificates
        </h3>
        
        {courseCompletions.length === 0 ? (
          <div style={{ 
            background: "#1a1a2a", 
            padding: "30px", 
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "15px" }}>📚</div>
            <p style={{ color: "#aaa", margin: 0 }}>
              Complete course exams with 70% or higher to earn certificates.
            </p>
            <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "10px" }}>
              Go to Library → Select a subject → Take the Chapter Exam
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {courseCompletions.map((completion, index) => (
              <div 
                key={index}
                style={{
                  background: "#1a2a1a",
                  padding: "20px",
                  borderRadius: "8px",
                  borderLeft: "4px solid #90EE90",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "15px"
                }}
              >
                <div>
                  <h4 style={{ color: "#FFD700", margin: "0 0 5px 0" }}>
                    {completion.subject_name}
                  </h4>
                  <p style={{ color: "#aaa", margin: 0, fontSize: "0.9rem" }}>
                    {completion.grade_level} • Score: <strong style={{ color: "#90EE90" }}>{completion.score}%</strong>
                  </p>
                  <p style={{ color: "#666", margin: "5px 0 0 0", fontSize: "0.8rem" }}>
                    Completed: {new Date(completion.completed_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedCertificate(completion)}
                  data-testid={`view-certificate-${completion.subject_id}`}
                  style={{
                    background: "#FFD700",
                    color: "#000",
                    padding: "10px 20px",
                    marginTop: 0
                  }}
                >
                  🏆 View Certificate
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ordination Certificate Section */}
      <div className="card no-print">
        <h3 style={{ color: "#FFD700", marginBottom: "20px" }}>
          ✝️ Ordination Certificate
        </h3>
        
        {!certificateData?.eligible ? (
          <div style={{
            background: "#1a1a2a",
            padding: "30px",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "15px" }}>🔒</div>
            <p style={{ color: "#aaa", margin: 0 }}>
              {certificateData?.message || "Complete the Ordination Program to receive your ordination certificate."}
            </p>
            <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "10px" }}>
              Go to Ordination → Complete all courses → Pass Final Exam
            </p>
          </div>
        ) : (
          <div>
            <p style={{ color: "#90EE90", marginBottom: "15px" }}>
              🎓 Congratulations! You are ordained and eligible to download your certificate.
            </p>
            <button data-testid="print-certificate-button" onClick={printCertificate}>
              🖨️ Print Ordination Certificate
            </button>
            {/* iter 68 — one-tap social share for Ordination */}
            {(() => {
              const shareText = encodeURIComponent(
                `I am now ordained through the Yisrael Learning Center Seminary program. #YisraelLearningCenter #Enoch72`
              );
              const shareUrl = encodeURIComponent(window.location.origin);
              const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}&summary=${shareText}`;
              const twitterUrl = `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`;
              const whatsappUrl = `https://api.whatsapp.com/send?text=${shareText}%20${shareUrl}`;
              const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`;
              return (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                  <a data-testid="share-ordination-linkedin" href={linkedinUrl} target="_blank" rel="noopener noreferrer" style={_shareBtn("#0a66c2")}>in LinkedIn</a>
                  <a data-testid="share-ordination-twitter" href={twitterUrl} target="_blank" rel="noopener noreferrer" style={_shareBtn("#1DA1F2")}>𝕏 Post</a>
                  <a data-testid="share-ordination-facebook" href={facebookUrl} target="_blank" rel="noopener noreferrer" style={_shareBtn("#1877F2")}>f Facebook</a>
                  <a data-testid="share-ordination-whatsapp" href={whatsappUrl} target="_blank" rel="noopener noreferrer" style={_shareBtn("#25D366")}>💬 WhatsApp</a>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Ordination Certificate Content - Shows on print */}
      {certificateData?.eligible && (
        <div className="certificate" data-testid="certificate-content" style={{
          background: "linear-gradient(135deg, #fff 0%, #f5f5dc 100%)",
          padding: "60px",
          textAlign: "center",
          border: "10px double #8B4513",
          margin: "20px auto",
          maxWidth: "850px",
          color: "#333"
        }}>
          <h2 style={{ 
            fontSize: "2.5rem", 
            color: "#8B4513",
            fontFamily: "serif",
            marginBottom: "30px"
          }}>
            ORDINATION CERTIFICATE
          </h2>
          
          <p style={{ fontSize: "1.2rem", marginTop: "30px" }}>This certifies that</p>
          
          <h3 style={{ fontSize: "2.5rem", margin: "20px 0", fontFamily: "serif" }}>
            {certificateData?.user_data?.username || user.username}
          </h3>
          
          <p style={{ fontSize: "1.1rem", margin: "20px 0" }}>
            has successfully completed all required studies
          </p>
          
          <p style={{ fontSize: "1.1rem" }}>and is officially ordained by</p>
          
          <h3 style={{ fontSize: "2rem", margin: "20px 0", color: "#8B4513", fontFamily: "serif" }}>
            {certificateData?.user_data?.church_name || config?.CHURCH_NAME || "Twelve Tribes of Yisrael"}
          </h3>
          
          <p style={{ fontSize: "1rem", color: "#666", margin: "10px 0" }}>
            Established {certificateData?.user_data?.established || config?.ESTABLISHED || 2012}
          </p>
          
          {/* Official Seal */}
          <div style={{ margin: "30px auto" }}>
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/22002ca9-24bb-4912-b3b6-4eb4ba6d87ae/images/5ea2270e4415f9b16eb9c3d5a9c6e790c2d8b49b4cb12d7e58da108a0bb70462.png"
              alt="Official Seal - Yisrael Learning Center"
              style={{
                width: "150px",
                height: "150px",
                margin: "0 auto",
                display: "block"
              }}
            />
          </div>
          
          <div style={{ 
            marginTop: "40px", 
            paddingTop: "20px", 
            borderTop: "2px solid #ccc",
            display: "flex",
            justifyContent: "space-around",
            alignItems: "flex-end",
            fontSize: "0.9rem"
          }}>
            <div style={{ textAlign: "center" }}>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/22002ca9-24bb-4912-b3b6-4eb4ba6d87ae/images/b19c66f1ad1e9da859918d3a7bbee781b3726e04c5d31cd64e2522dddbaddfef.png"
                alt="Signature"
                style={{
                  width: "160px",
                  height: "auto",
                  marginBottom: "5px"
                }}
              />
              <div style={{ 
                borderTop: "2px solid #000", 
                width: "200px", 
                margin: "0 auto 5px" 
              }}></div>
              <p style={{ fontWeight: "bold", color: "#333" }}>Chief/Priest Ykbn Yoonw</p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ 
                fontSize: "1.2rem", 
                fontWeight: "bold", 
                color: "#333",
                marginBottom: "10px"
              }}>
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </p>
              <div style={{ 
                borderTop: "2px solid #000", 
                width: "200px", 
                margin: "0 auto 5px" 
              }}></div>
              <p style={{ fontWeight: "bold", color: "#333" }}>Date of Ordination</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificateView;

// iter 68 — social-share button style helper. One tap per platform.
// Keeps the CertificateView otherwise untouched (per the strict
// "hands-off Certificate Engine" mandate — we only add outbound
// share links, we never mutate the certificate document itself).
const _shareBtn = (bg) => ({
  padding: "10px 16px",
  borderRadius: 6,
  background: bg,
  color: "#fff",
  fontWeight: 700,
  fontSize: "0.85rem",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "none",
  cursor: "pointer",
});
